/**
 * AdminNotificationPreferences — Super-admin screen to toggle a Person's
 * notification channels (email/sms/whatsapp) and per-event overrides.
 *
 * Route: /admin/notifications/preferences/:id
 *
 * Uses GET/PUT /api/admin/notifications/preferences/{person_id}.
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Bell, Mail, MessageSquare, Smartphone, ArrowLeft, Save, Trash2,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const KNOWN_EVENTS = [
  'Payment_Confirmed',
  'Document_Ready',
  'Registration_Completed',
  'Student_Graduated',
  'Fee_Payment_Received',
  'Room_Assigned',
];

const CHANNELS = [
  { key: 'email',    label: 'Email',    icon: Mail },
  { key: 'sms',      label: 'SMS',      icon: MessageSquare },
  { key: 'whatsapp', label: 'WhatsApp', icon: Smartphone },
];

export default function AdminNotificationPreferences() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [channels, setChannels] = useState({ email: true, sms: false, whatsapp: false });
  const [eventTypes, setEventTypes] = useState({});
  const [newEventName, setNewEventName] = useState('');

  const personQ = useQuery({
    queryKey: ['person-brief', id],
    queryFn: async () => {
      const r = await axios.get(
        `${API}/api/admin/identity-registry/persons/${id}`,
        { headers: authH() },
      );
      return r.data;
    },
  });

  const prefsQ = useQuery({
    queryKey: ['notif-prefs', id],
    queryFn: async () => {
      const r = await axios.get(
        `${API}/api/admin/notifications/preferences/${id}`,
        { headers: authH() },
      );
      return r.data;
    },
  });

  useEffect(() => {
    if (prefsQ.data) {
      setChannels({ email: true, sms: false, whatsapp: false, ...(prefsQ.data.channels || {}) });
      setEventTypes(prefsQ.data.event_types || {});
    }
  }, [prefsQ.data]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const r = await axios.put(
        `${API}/api/admin/notifications/preferences/${id}`,
        { channels, event_types: eventTypes },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: () => toast.success('Preferences saved'),
    onError: (e) => toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`),
  });

  const person = personQ.data?.person || {};
  const displayName = (person.first_name || person.last_name)
    ? `${person.first_name || ''} ${person.last_name || ''}`.trim()
    : (person.name || person.username || 'Person');

  const toggleChannel = (k) => setChannels((c) => ({ ...c, [k]: !c[k] }));
  const toggleEventChannel = (evt, k) => {
    setEventTypes((prev) => {
      const cur = { ...(prev[evt] || {}) };
      cur[k] = !cur[k];
      return { ...prev, [evt]: cur };
    });
  };
  const clearOverride = (evt) => {
    setEventTypes((prev) => {
      const cp = { ...prev };
      delete cp[evt];
      return cp;
    });
  };
  const addEventOverride = () => {
    const name = newEventName.trim();
    if (!name) return;
    setEventTypes((prev) => ({ ...prev, [name]: prev[name] || {} }));
    setNewEventName('');
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-notif-prefs-page">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex items-center justify-between">
          <Button
            data-testid="prefs-back-btn"
            variant="outline"
            className="border-slate-700 bg-slate-900 hover:bg-slate-800"
            onClick={() => navigate(`/admin/persons/${id}`)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Person profile
          </Button>
          <Button
            data-testid="prefs-save-btn"
            className="bg-violet-600 hover:bg-violet-500"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
          >
            <Save className="mr-2 h-4 w-4" /> {saveMut.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Bell className="h-8 w-8 text-violet-400" />
              <div>
                <h1 className="text-2xl font-bold" data-testid="prefs-person-name">{displayName}</h1>
                <p className="text-sm text-slate-400">{person.email || person.username || id}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader>
            <CardTitle className="text-base">Default channels</CardTitle>
            <p className="text-xs text-slate-500">Applied to every event unless overridden below.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {CHANNELS.map(({ key, label, icon: Icon }) => (
                <div
                  key={key}
                  data-testid={`channel-${key}`}
                  className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/60 p-3"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-slate-400" />
                    <span className="font-medium">{label}</span>
                  </div>
                  <Switch
                    data-testid={`channel-switch-${key}`}
                    checked={!!channels[key]}
                    onCheckedChange={() => toggleChannel(key)}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader>
            <CardTitle className="text-base">Per-event overrides</CardTitle>
            <p className="text-xs text-slate-500">
              Per-event settings win over defaults. Leave an event out to inherit the defaults.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {KNOWN_EVENTS.concat(
              Object.keys(eventTypes).filter((e) => !KNOWN_EVENTS.includes(e)),
            ).map((evt) => {
              const override = eventTypes[evt];
              const hasOverride = !!override;
              return (
                <div
                  key={evt}
                  data-testid={`event-row-${evt}`}
                  className="rounded-lg border border-slate-800 bg-slate-950/60 p-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{evt}</span>
                      {hasOverride && (
                        <Badge variant="outline" className="border-violet-500/40 text-violet-300 text-xs">
                          override
                        </Badge>
                      )}
                    </div>
                    {hasOverride && (
                      <Button
                        data-testid={`clear-override-${evt}`}
                        size="sm"
                        variant="ghost"
                        className="text-rose-400 hover:text-rose-300"
                        onClick={() => clearOverride(evt)}
                      >
                        <Trash2 className="mr-1 h-3 w-3" /> Clear override
                      </Button>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {CHANNELS.map(({ key, label }) => {
                      const isOverridden = hasOverride && override[key] !== undefined;
                      const isOn = isOverridden ? !!override[key] : !!channels[key];
                      return (
                        <label
                          key={key}
                          data-testid={`event-${evt}-${key}`}
                          className={`flex items-center justify-between rounded border px-3 py-2 text-sm cursor-pointer ${
                            isOn
                              ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-300'
                              : 'border-slate-700 bg-slate-950 text-slate-400'
                          } ${isOverridden ? 'ring-1 ring-violet-500/40' : ''}`}
                        >
                          <span>{label}</span>
                          <Switch
                            checked={isOn}
                            onCheckedChange={() => toggleEventChannel(evt, key)}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="flex items-end gap-2 pt-2">
              <div className="flex-1">
                <label className="text-xs text-slate-400">Add custom event override</label>
                <Input
                  data-testid="new-event-input"
                  value={newEventName}
                  onChange={(e) => setNewEventName(e.target.value)}
                  placeholder="e.g. Room_Assigned"
                  className="mt-1 border-slate-700 bg-slate-950"
                />
              </div>
              <Button
                data-testid="add-event-btn"
                onClick={addEventOverride}
                variant="outline"
                className="border-slate-700 bg-slate-900 hover:bg-slate-800"
              >
                Add
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
